import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '../../components/layout/DashboardLayout';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { 
  Award, Search, Gift, Star, Users, TrendingUp,
  Phone, RefreshCw, Crown, Bus
} from 'lucide-react';
import { format, parseISO } from 'date-fns';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const TIER_COLORS = {
  Bronze: 'bg-orange-100 text-orange-800 border-orange-300',
  Silver: 'bg-slate-100 text-slate-800 border-slate-300',
  Gold: 'bg-yellow-100 text-yellow-800 border-yellow-300',
  Platinum: 'bg-purple-100 text-purple-800 border-purple-300'
};

const TIER_ICONS = {
  Bronze: Star,
  Silver: Star,
  Gold: Crown,
  Platinum: Crown
};

const LoyaltyPage = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  
  const [searchPhone, setSearchPhone] = useState('');
  const [loyaltyStatus, setLoyaltyStatus] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingLeaderboard, setLoadingLeaderboard] = useState(true);

  useEffect(() => {
    if (!user || !token) {
      navigate('/login');
      return;
    }
    if (['station_superuser', 'overseer'].includes(user.role)) {
      fetchLeaderboard();
    } else {
      setLoadingLeaderboard(false);
    }
  }, [user, token, navigate]);

  const fetchLeaderboard = async () => {
    try {
      const response = await axios.get(`${API_URL}/loyalty/leaderboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLeaderboard(response.data);
    } catch (error) {
      console.error('Failed to load leaderboard');
    } finally {
      setLoadingLeaderboard(false);
    }
  };

  const searchLoyalty = async () => {
    if (!searchPhone) {
      toast.error('Please enter a phone number');
      return;
    }
    
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/loyalty/status`, {
        params: { phone: searchPhone }
      });
      setLoyaltyStatus(response.data);
    } catch (error) {
      toast.error('Failed to check loyalty status');
    } finally {
      setLoading(false);
    }
  };

  const getTierProgress = (trips) => {
    if (trips >= 50) return { current: 'Platinum', next: null, progress: 100 };
    if (trips >= 25) return { current: 'Gold', next: 'Platinum', progress: ((trips - 25) / 25) * 100, needed: 50 - trips };
    if (trips >= 10) return { current: 'Silver', next: 'Gold', progress: ((trips - 10) / 15) * 100, needed: 25 - trips };
    return { current: 'Bronze', next: 'Silver', progress: (trips / 10) * 100, needed: 10 - trips };
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="font-heading text-2xl font-bold flex items-center gap-2">
            <Award className="h-7 w-7 text-panashe-gold" />
            Loyalty Program
          </h1>
          <p className="text-slate-500">Every 7th trip is FREE! Check customer loyalty status.</p>
        </div>

        {/* Loyalty Tiers Explanation */}
        <Card className="bg-gradient-to-r from-panashe-blue to-blue-700 text-white">
          <CardContent className="p-6">
            <h2 className="font-heading text-xl font-bold mb-4">Loyalty Tiers</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="text-center">
                <div className="w-12 h-12 bg-orange-400 rounded-full flex items-center justify-center mx-auto mb-2">
                  <Star className="h-6 w-6 text-white" />
                </div>
                <p className="font-bold">Bronze</p>
                <p className="text-sm text-white/70">0-9 trips</p>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 bg-slate-400 rounded-full flex items-center justify-center mx-auto mb-2">
                  <Star className="h-6 w-6 text-white" />
                </div>
                <p className="font-bold">Silver</p>
                <p className="text-sm text-white/70">10-24 trips</p>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 bg-yellow-400 rounded-full flex items-center justify-center mx-auto mb-2">
                  <Crown className="h-6 w-6 text-white" />
                </div>
                <p className="font-bold">Gold</p>
                <p className="text-sm text-white/70">25-49 trips</p>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 bg-purple-400 rounded-full flex items-center justify-center mx-auto mb-2">
                  <Crown className="h-6 w-6 text-white" />
                </div>
                <p className="font-bold">Platinum</p>
                <p className="text-sm text-white/70">50+ trips</p>
              </div>
            </div>
            <div className="mt-4 p-3 bg-white/10 rounded-lg">
              <Gift className="h-5 w-5 inline mr-2" />
              <strong>7th Trip Free:</strong> Every 7th trip is completely free for all loyalty members!
            </div>
          </CardContent>
        </Card>

        {/* Search Customer */}
        <Card>
          <CardHeader>
            <CardTitle>Check Customer Loyalty Status</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex gap-4">
              <div className="flex-1">
                <Label>Customer Phone Number</Label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    placeholder="+263 77 123 4567"
                    value={searchPhone}
                    onChange={(e) => setSearchPhone(e.target.value)}
                    className="pl-10"
                    data-testid="loyalty-phone-input"
                  />
                </div>
              </div>
              <Button 
                className="btn-panashe mt-6" 
                onClick={searchLoyalty}
                disabled={loading}
                data-testid="check-loyalty-btn"
              >
                {loading ? <RefreshCw className="h-4 w-4 animate-spin mr-2" /> : <Search className="h-4 w-4 mr-2" />}
                Check Status
              </Button>
            </div>

            {/* Loyalty Result */}
            {loyaltyStatus && (
              <div className="mt-6 p-6 bg-slate-50 rounded-lg">
                <div className="flex items-start justify-between">
                  <div>
                    {loyaltyStatus.name && (
                      <p className="text-lg font-bold">{loyaltyStatus.name}</p>
                    )}
                    <p className="text-slate-500">{loyaltyStatus.phone}</p>
                  </div>
                  <Badge className={`text-lg px-4 py-2 ${TIER_COLORS[loyaltyStatus.tier]}`}>
                    {loyaltyStatus.tier}
                  </Badge>
                </div>

                <div className="grid grid-cols-3 gap-4 mt-4">
                  <div className="text-center p-4 bg-white rounded-lg">
                    <p className="text-3xl font-bold text-panashe-blue">{loyaltyStatus.trips_completed}</p>
                    <p className="text-sm text-slate-500">Trips Completed</p>
                  </div>
                  <div className="text-center p-4 bg-white rounded-lg">
                    <p className="text-3xl font-bold text-green-600">{loyaltyStatus.next_free_trip}</p>
                    <p className="text-sm text-slate-500">Trips to Free</p>
                  </div>
                  <div className="text-center p-4 bg-white rounded-lg">
                    <p className="text-3xl font-bold text-panashe-gold">${loyaltyStatus.total_spend_usd?.toFixed(0) || 0}</p>
                    <p className="text-sm text-slate-500">Total Spent</p>
                  </div>
                </div>

                {loyaltyStatus.is_eligible_for_free && (
                  <div className="mt-4 p-4 bg-green-100 border border-green-300 rounded-lg text-center">
                    <Gift className="h-8 w-8 text-green-600 mx-auto mb-2" />
                    <p className="text-green-800 font-bold text-lg">🎉 Eligible for FREE Trip!</p>
                    <p className="text-green-700 text-sm">This customer's next booking will be free!</p>
                  </div>
                )}

                {/* Progress to next tier */}
                {(() => {
                  const progress = getTierProgress(loyaltyStatus.trips_completed);
                  if (!progress.next) return null;
                  
                  return (
                    <div className="mt-4">
                      <div className="flex justify-between text-sm mb-1">
                        <span>Progress to {progress.next}</span>
                        <span>{progress.needed} trips to go</span>
                      </div>
                      <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-panashe-blue rounded-full transition-all"
                          style={{ width: `${progress.progress}%` }}
                        />
                      </div>
                    </div>
                  );
                })()}

                {/* Passenger Trip History */}
                <div className="mt-6 border-t pt-4">
                  <h3 className="font-heading font-bold text-base text-slate-900 mb-3 flex items-center gap-2">
                    <Bus className="h-4 w-4 text-panashe-blue" />
                    Trip History ({loyaltyStatus.trip_history?.length || 0})
                  </h3>
                  {loyaltyStatus.trip_history && loyaltyStatus.trip_history.length > 0 ? (
                    <div className="space-y-2 max-h-80 overflow-y-auto pr-1">
                      {loyaltyStatus.trip_history.map((t, i) => (
                        <div 
                          key={i} 
                          className="bg-white p-3 rounded-lg border border-slate-200 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 text-xs hover:border-panashe-blue/40 transition-colors"
                        >
                          <div className="space-y-0.5">
                            <div className="flex items-center gap-2 font-bold text-slate-900 text-sm">
                              <span className="text-panashe-blue">{t.origin}</span>
                              <span className="text-slate-400">→</span>
                              <span className="text-green-700">{t.destination}</span>
                              <span className="font-mono text-xs bg-slate-100 text-slate-700 px-1.5 py-0.5 rounded ml-1">{t.booking_ref}</span>
                            </div>
                            <div className="flex items-center gap-3 text-slate-500">
                              <span>Seat(s): <strong className="text-slate-700">{t.seats?.join(', ') || 'N/A'}</strong></span>
                              <span>Date: <strong>{t.departure_datetime ? format(parseISO(t.departure_datetime), 'dd MMM yyyy HH:mm') : 'N/A'}</strong></span>
                            </div>
                          </div>
                          <div className="flex items-center gap-3 self-end sm:self-center">
                            <div className="text-right">
                              <p className="font-bold text-slate-900">{t.currency === 'ZAR' ? 'R' : '$'}{Number(t.total_amount || 0).toFixed(2)}</p>
                            </div>
                            <Badge className={
                              t.payment_status === 'paid' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'
                            }>
                              {t.payment_status?.toUpperCase()}
                            </Badge>
                            <Button 
                              size="xs" 
                              variant="outline"
                              className="text-[10px] h-7 px-2"
                              onClick={() => window.open(`/ticket/${t.booking_ref}`, '_blank')}
                            >
                              View Ticket
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-xs text-slate-500 italic py-2">No completed trips found for this customer.</p>
                  )}
                </div>
              </div>
            )}

          </CardContent>
        </Card>

        {/* Leaderboard */}
        {['station_superuser', 'overseer'].includes(user?.role) && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="h-5 w-5" />
                Top Loyal Customers
              </CardTitle>
            </CardHeader>
            <CardContent>
              {loadingLeaderboard ? (
                <div className="flex items-center justify-center py-8">
                  <RefreshCw className="h-6 w-6 animate-spin text-panashe-blue" />
                </div>
              ) : leaderboard.length === 0 ? (
                <p className="text-center text-slate-500 py-8">No loyalty data yet</p>
              ) : (
                <div className="space-y-2">
                  {leaderboard.slice(0, 10).map((customer, idx) => {
                    const TierIcon = TIER_ICONS[customer.tier] || Star;
                    return (
                      <div 
                        key={idx}
                        className="flex items-center justify-between p-3 bg-slate-50 rounded-lg"
                      >
                        <div className="flex items-center gap-3">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold ${
                            idx === 0 ? 'bg-yellow-400 text-yellow-900' :
                            idx === 1 ? 'bg-slate-300 text-slate-700' :
                            idx === 2 ? 'bg-orange-400 text-orange-900' :
                            'bg-slate-100 text-slate-600'
                          }`}>
                            {idx + 1}
                          </div>
                          <div>
                            <p className="font-medium">{customer.name}</p>
                            <p className="text-xs text-slate-500">{customer.phone}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          <div className="text-right">
                            <p className="font-bold">{customer.trips} trips</p>
                            <p className="text-xs text-slate-500">${customer.total_spend?.toFixed(0) || 0} spent</p>
                          </div>
                          <Badge className={TIER_COLORS[customer.tier]}>
                            <TierIcon className="h-3 w-3 mr-1" />
                            {customer.tier}
                          </Badge>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
};

export default LoyaltyPage;
